import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Select from "react-select";
import "./DepositForm.css";

const Edit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [depositSections, setDepositSections] = useState([]);
  const [depositSubSections, setDepositSubSections] = useState([]);
  const [formData, setFormData] = useState({});
  const [existingItems, setExistingItems] = useState([]); // Store existing items with IDs
  const [shift, setShift] = useState("Day Shift");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({});
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  // Dynamic time slots based on shift
  const getTimeSlots = (shiftType) => {
    if (shiftType === "Day Shift") {
      return [
        "5:00 am",
        "6:00 am",
        "7:00 am",
        "8:00 am", 
        "9:00 am",
        "10:00 am",
        "11:00 am",
        "12:00 pm",
        "1:00 pm",
        "2:00 pm",
        "3:00 pm",
        "4:00 pm",
        "Closing",
        "Total"
      ];
    } else { // Night Shift
      return [
        "4:00 pm",
        "5:00 pm",
        "6:00 pm",
        "7:00 pm",
        "8:00 pm",
        "9:00 pm",
        "10:00 pm",
        "11:00 pm",
        "12:00 am",
        "1:00 am",
        "2:00 am",
        "3:00 am",
        "4:00 am",
        "5:00 am",
        "Closing",
        "Total"
      ];
    }
  };

  const [timeSlots, setTimeSlots] = useState(getTimeSlots("Day Shift"));

  // Static columns that appear after dynamic columns (aligned with Create.js)
  const staticColumns = [
    "Bus Staff","Grant Total"
  ];

  // Add duplicateExists state
  const [duplicateExists, setDuplicateExists] = useState(false);
  // Fetch banks for Bank select field
  useEffect(() => {
    setLoadingBanks(true);
    Http.get("/accounts/banks")
      .then((res) => setBanks(res.data))
      .catch((err) => console.error("Fetch banks error:", err))
      .finally(() => setLoadingBanks(false));
  }, []);

  // Fetch deposit sections, subsections, and existing form data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch sections
        const sectionsResponse = await Http.get("/accounts/deposit-section");
        setDepositSections(sectionsResponse.data);

        // Fetch subsections
        const subSectionsResponse = await Http.get("/accounts/deposit-sub-section");
        setDepositSubSections(subSectionsResponse.data);

        // Fetch existing deposit form data
        const formResponse = await Http.get(`/accounts/deposit-forms/${id}`);
        const existingForm = formResponse.data.data;
        // Set basic form fields
        setShift(existingForm.shift);
        setDate(existingForm.date);
        setNotes(existingForm.notes || "");
        setSelectedBankId(existingForm.bank_id || null);
        // Store existing items with their IDs
        setExistingItems(existingForm.items || []);

        // Initialize form data structure with existing data
        const currentTimeSlots = getTimeSlots(existingForm.shift);
        setTimeSlots(currentTimeSlots);
        
        // Initialize form data
        const initializedData = {};
        currentTimeSlots.forEach(timeSlot => {
          initializedData[timeSlot] = {};
          
          // Initialize dynamic columns (sections and subsections)
          sectionsResponse.data.forEach(section => {
            const sectionSubSections = subSectionsResponse.data.filter(sub => sub.deposit_section_id === section.id);
            
            if (sectionSubSections.length > 0) {
              sectionSubSections.forEach(subSection => {
                const columnKey = `section_${section.id}_subsection_${subSection.id}`;
                initializedData[timeSlot][columnKey] = existingForm.form_data[timeSlot]?.[columnKey] || '';
              });
            } else {
              const columnKey = `section_${section.id}`;
              initializedData[timeSlot][columnKey] = existingForm.form_data[timeSlot]?.[columnKey] || '';
            }
          });
          
          // Initialize static columns
          staticColumns.forEach(column => {
            const columnKey = column.toLowerCase().replace(/\s+/g, '_');
            initializedData[timeSlot][columnKey] = existingForm.form_data[timeSlot]?.[columnKey] || '';
          });
        });
        
        // Store the data in the appropriate shift state
        if (existingForm.shift === "Day Shift") {
          setDayShiftData(initializedData);
        } else {
          setNightShiftData(initializedData);
        }
        
        setFormData(initializedData);
        
      } catch (error) {
        console.error("Error fetching data:", error);
        setError({ general: "Failed to load form data" });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  // Proactively check duplicate when date or shift changes (exclude current form id)
  useEffect(() => {
    if (!date || !shift) return;
    let cancelled = false;
    Http.get('/accounts/deposit-forms-check', { params: { date, shift, ignore_id: id } })
      .then((res) => {
        if (cancelled) return;
        const { exists, message } = res.data || {};
        setDuplicateExists(!!exists);
        setError((prev) => ({ ...prev, general: exists ? message : '' }));
      })
      .catch(() => {
        if (cancelled) return;
        setDuplicateExists(false);
      });
    return () => { cancelled = true; };
  }, [date, shift, id]);

  // Initialize form data with existing values or empty values for all combinations
  const initializeFormData = (sections, subSections, existingData = {}, currentTimeSlots = timeSlots) => {
    const initialData = {};
    
    currentTimeSlots.forEach(timeSlot => {
      initialData[timeSlot] = {};
      
      // Initialize dynamic columns (sections and subsections)
      sections.forEach(section => {
        const sectionSubSections = subSections.filter(sub => sub.deposit_section_id === section.id);
        
        if (sectionSubSections.length > 0) {
          sectionSubSections.forEach(subSection => {
            const columnKey = `section_${section.id}_subsection_${subSection.id}`;
            initialData[timeSlot][columnKey] = existingData[timeSlot]?.[columnKey] || '';
          });
        } else {
          const columnKey = `section_${section.id}`;
          initialData[timeSlot][columnKey] = existingData[timeSlot]?.[columnKey] || '';
        }
      });
      
      // Initialize static columns
      staticColumns.forEach(column => {
        const columnKey = column.toLowerCase().replace(/\s+/g, '_');
        initialData[timeSlot][columnKey] = existingData[timeSlot]?.[columnKey] || '';
      });
    });
    
    setFormData(initialData);
  };

  // Store original form data for each shift type
  const [dayShiftData, setDayShiftData] = useState({});
  const [nightShiftData, setNightShiftData] = useState({});

  // Handle shift change
  const handleShiftChange = (newShift) => {
    // Save current form data to the appropriate shift storage
    if (shift === "Day Shift") {
      setDayShiftData({...formData});
    } else if (shift === "Night Shift") {
      setNightShiftData({...formData});
    }
    
    setShift(newShift);
    const newTimeSlots = getTimeSlots(newShift);
    setTimeSlots(newTimeSlots);
    
    // Check if we have stored data for the new shift
    const storedData = newShift === "Day Shift" ? dayShiftData : nightShiftData;
    const hasStoredData = Object.keys(storedData).length > 0;
    
    // Use stored data if available, otherwise initialize new data
    if (hasStoredData) {
      setFormData(storedData);
    } else if (depositSections.length > 0 && depositSubSections.length > 0) {
      initializeFormData(depositSections, depositSubSections, {}, newTimeSlots);
    }
  };

  // Handle input changes
  const handleInputChange = (timeSlot, columnKey, value) => {
    setFormData(prev => ({
      ...prev,
      [timeSlot]: {
        ...prev[timeSlot],
        [columnKey]: parseFloat(value) || 0
      }
    }));
  };

  // Calculate totals for each time slot (only for Total column - sum of W. Sales, Diposite, Short, Bus Staff)
  const calculateRowTotal = (timeSlot) => {
    if (!formData[timeSlot]) return 0;
    
    const totalColumns = ['w._sales', 'diposite', 'short', 'bus_staff'];
    return totalColumns.reduce((total, key) => {
      return total + (parseFloat(formData[timeSlot][key]) || 0);
    }, 0);
  };

  // Calculate grand total for each time slot (sum of all inputs in the row)
  const calculateGrandTotal = (timeSlot) => {
    if (!formData[timeSlot]) return 0;
    
    return Object.keys(formData[timeSlot]).reduce((total, key) => {
      if (key !== 'total' && key !== 'grant_total') {
        return total + (parseFloat(formData[timeSlot][key]) || 0);
      }
      return total;
    }, 0);
  };

  // Calculate column totals
  const calculateColumnTotal = (columnKey) => {
    return timeSlots.slice(0, -1).reduce((total, timeSlot) => { // Exclude "Total" row
      if (formData[timeSlot] && formData[timeSlot][columnKey]) {
        return total + (parseFloat(formData[timeSlot][columnKey]) || 0);
      }
      return total;
    }, 0);
  };

  // Update totals whenever form data changes
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      const updatedData = { ...formData };
      
      // Update row totals and grand totals
      timeSlots.forEach(timeSlot => {
        if (timeSlot !== "Total" && updatedData[timeSlot]) {
          updatedData[timeSlot].total = calculateRowTotal(timeSlot);
          updatedData[timeSlot].grant_total = calculateGrandTotal(timeSlot);
        }
      });
      
      // Update column totals in "Total" row
      if (updatedData["Total"]) {
        // Calculate totals for dynamic columns
        depositSections.forEach(section => {
          const sectionSubSections = depositSubSections.filter(sub => sub.deposit_section_id === section.id);
          
          if (sectionSubSections.length > 0) {
            sectionSubSections.forEach(subSection => {
              const columnKey = `section_${section.id}_subsection_${subSection.id}`;
              updatedData["Total"][columnKey] = calculateColumnTotal(columnKey);
            });
          } else {
            const columnKey = `section_${section.id}`;
            updatedData["Total"][columnKey] = calculateColumnTotal(columnKey);
          }
        });
        
        // Calculate totals for static columns
        staticColumns.forEach(column => {
          const columnKey = column.toLowerCase().replace(/\s+/g, '_');
          if (columnKey !== 'total' && columnKey !== 'grant_total') {
            updatedData["Total"][columnKey] = calculateColumnTotal(columnKey);
          }
        });
        
        // Calculate grand total for "Total" row
        updatedData["Total"].total = calculateRowTotal("Total");
        updatedData["Total"].grant_total = calculateGrandTotal("Total");
      }
      
      setFormData(updatedData);
    }
  }, [formData, depositSections, depositSubSections]);

  // Transform form data to items array for backend
  const transformFormDataToItems = () => {
    const items = [];
    
    Object.keys(formData).forEach(timeSlot => {
      if (timeSlot === "Total") return; // Skip total row for items
      
      Object.keys(formData[timeSlot]).forEach(columnKey => {
        const value = parseFloat(formData[timeSlot][columnKey]) || 0;
        
        if (value > 0) { // Only include non-zero values
          let item = {
            time_slot: timeSlot,
            column_key: columnKey,
            value: value
          };
          
          // Find existing item ID if it exists
          const existingItem = existingItems.find(existing => 
            existing.time_slot === timeSlot && 
            existing.column_key === columnKey
          );
          
          if (existingItem) {
            item.id = existingItem.id;
          }
          
          // Determine column type and IDs
          if (columnKey.startsWith('section_')) {
            if (columnKey.includes('_subsection_')) {
              // Extract section and subsection IDs
              const matches = columnKey.match(/section_(\d+)_subsection_(\d+)/);
              if (matches) {
                item.column_type = 'subsection';
                item.deposit_section_id = parseInt(matches[1]);
                item.deposit_sub_section_id = parseInt(matches[2]);
              }
            } else {
              // Extract section ID only
              const matches = columnKey.match(/section_(\d+)/);
              if (matches) {
                item.column_type = 'section';
                item.deposit_section_id = parseInt(matches[1]);
                item.deposit_sub_section_id = null;
              }
            }
          } else {
            // Static columns
            item.column_type = 'static';
            item.deposit_section_id = null;
            item.deposit_sub_section_id = null;
          }
          
          items.push(item);
        }
      });
    });
    
    return items;
  };

  // Calculate total amounts
  const calculateTotalAmounts = () => {
    let totalAmount = 0;
    let grandTotalAmount = 0;
    
    // Calculate from the "Total" row
    if (formData["Total"]) {
      totalAmount = parseFloat(formData["Total"].total) || 0;
      grandTotalAmount = parseFloat(formData["Total"].grant_total) || 0;
    }
    
    return { totalAmount, grandTotalAmount };
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const items = transformFormDataToItems();
      const { totalAmount, grandTotalAmount } = calculateTotalAmounts();
      
      const payload = {
        form_data: formData,
        items: items,
        total_amount: totalAmount,
        grand_total_amount: grandTotalAmount,
        shift: shift,
        date: date,
        bank_id: selectedBankId,
        notes: notes
      };
      
      const response = await Http.put(`/accounts/deposit-forms/${id}`, payload);
      
      navigate("/daily-collection", {
        state: { message: "Daily collection updated successfully!", type: "success" }
      });
    } catch (error) {
      console.error("Error updating form:", error);
      setError({ general: "Failed to update form" });
    }
  };

  // Group subsections by section
  const getSectionColumns = () => {
    const columns = [];
    
    depositSections.forEach(section => {
      const sectionSubSections = depositSubSections.filter(sub => sub.deposit_section_id === section.id);
      
      if (sectionSubSections.length > 0) {
        columns.push({
          type: 'section',
          section: section,
          subSections: sectionSubSections
        });
      } else {
        columns.push({
          type: 'section',
          section: section,
          subSections: []
        });
      }
    });
    
    return columns;
  };

  if (loading) {
    return (
      <div className="deposit-form-container">
        <IsLogin />
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title deposit-form-title">Edit Daily Collection</h5>
          </div>
          <div className="am-pagebody">
            <div className="card deposit-form-card pd-20 pd-sm-40">
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <div className="loading-text">Loading form data...</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sectionColumns = getSectionColumns();

  return (
    <div className="deposit-form-container">
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title deposit-form-title">Edit Daily Collection</h5>
        </div>
        <div className="am-pagebody">
          <div className="card deposit-form-card pd-20 pd-sm-40">
            {error.general && (
              <div className="alert alert-danger alert-custom alert-danger-custom">{error.general}</div>
            )}
            {/* Date, Bank, and Shift */}
            <div className="row mb-4">
              <div className="col-md-3">
                <label htmlFor="date" className="form-label fw-bold">Date:</label>
                <input
                  type="date"
                  id="date"
                  className="form-control"
                  value={date ? new Date(date).toISOString().split('T')[0] : ''}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>

              <div className="col-md-5">
                <label className="form-label fw-bold">Bank <span className="text-danger">*</span></label>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  name="bank_id"
                  value={
                    banks
                      .map((bank) => ({
                        value: bank.id,
                        label: bank.account_number ? `${bank.name} - ${bank.account_number}` : bank.name,
                      }))
                      .find((option) => option.value === selectedBankId) || null
                  }
                  options={banks.map((bank) => ({
                    value: bank.id,
                    label: bank.account_number ? `${bank.name} - ${bank.account_number}` : bank.name,
                  }))}
                  onChange={(selectedOption) => setSelectedBankId(selectedOption.value)}
                  isLoading={loadingBanks}
                  placeholder="Select Bank"
                />
              </div>

              <div className="col-md-3">
                <label htmlFor="shift" className="form-label fw-bold">Select Shift:</label>
                <select 
                  id="shift"
                  className="form-select"
                  value={shift}
                  onChange={(e) => handleShiftChange(e.target.value)}
                >
                  <option value="Day Shift">Day Shift</option>
                  <option value="Night Shift">Night Shift</option>
                </select>
              </div>
            </div>
            {/* <div className="col-md-6">
              <label htmlFor="notes" className="form-label fw-bold">Notes:</label>
              <textarea
                id="notes"
                className="form-control"
                rows="2"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes..."
              />
            </div> */}
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="table-responsive">
                <table className="table table-bordered table-striped deposit-table">
                  <thead>
                    <tr>
                      <th rowSpan="2" className="align-middle time-cell">Time</th>
                      
                      {/* Dynamic section headers */}
                      {sectionColumns.map((column, index) => (
                        <th 
                          key={`section-${column.section.id}`}
                          colSpan={column.subSections.length || 1}
                          className="text-center section-header"
                        >
                          Section- {column.section.name}
                        </th>
                      ))}
                      
                      {/* Static column headers */}
                      {staticColumns.map((column, index) => (
                        <th 
                          key={`static-${index}`}
                          rowSpan="2" 
                          className="align-middle text-center static-header"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                    
                    <tr>
                      {/* Dynamic subsection headers */}
                      {sectionColumns.map((column) => 
                        column.subSections.length > 0 
                          ? column.subSections.map((subSection) => (
                              <th 
                                key={`subsection-${subSection.id}`}
                                className="text-center subsection-header"
                              >
                                {subSection.name}
                              </th>
                            ))
                          : (
                              <th 
                                key={`section-only-${column.section.id}`}
                                className="text-center subsection-header"
                              >
                                {column.section.name}
                              </th>
                            )
                      )}
                    </tr>
                  </thead>
                  
                  <tbody>
                    {timeSlots.map((timeSlot, timeIndex) => (
                      <tr key={timeSlot} className={timeSlot === "Total" ? "total-row" : ""}>
                        <td className="time-cell">{timeSlot}</td>
                        
                        {/* Dynamic columns */}
                        {sectionColumns.map((column) => 
                          column.subSections.length > 0 
                            ? column.subSections.map((subSection) => {
                                const columnKey = `section_${column.section.id}_subsection_${subSection.id}`;
                                return (
                                  <td key={columnKey}>
                                    <input
                                      type="number"
                                      className="deposit-input"
                                      value={formData[timeSlot]?.[columnKey] || ''}
                                      onChange={(e) => handleInputChange(timeSlot, columnKey, e.target.value)}
                                      disabled={timeSlot === "Total"}
                                      min="0"
                                      step="0.01"
                                      placeholder=""
                                    />
                                  </td>
                                );
                              })
                            : (
                                <td key={`section-only-${column.section.id}`}>
                                  <input
                                    type="number"
                                    className="deposit-input"
                                    value={formData[timeSlot]?.[`section_${column.section.id}`] || ''}
                                    onChange={(e) => handleInputChange(timeSlot, `section_${column.section.id}`, e.target.value)}
                                    disabled={timeSlot === "Total"}
                                    min="0"
                                    step="0.01"
                                    placeholder=""
                                  />
                                </td>
                              )
                        )}
                        
                        {/* Static columns */}
                        {staticColumns.map((column, index) => {
                          const columnKey = column.toLowerCase().replace(/\s+/g, '_');
                          return (
                            <td key={`static-${index}`}>
                              <input
                                type="number"
                                className="deposit-input"
                                value={formData[timeSlot]?.[columnKey] || ''}
                                onChange={(e) => handleInputChange(timeSlot, columnKey, e.target.value)}
                                disabled={timeSlot === "Total" || columnKey === 'total' || columnKey === 'grant_total'}
                                min="0"
                                step="0.01"
                                placeholder=""
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="deposit-form-buttons">
                <button type="submit" className="btn btn-submit mr-3">
                  Update Form
                </button>
                <button 
                  type="button" 
                  className="btn btn-cancel"
                  onClick={() => navigate("/daily-collection")}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  );
};

export default Edit;